import { Microfrontend } from '@cian/mf-react';
import { type ICianMFUIElement } from '@cian/mf-registry/browser';
import { Button, ModalWindow, Spinner, UIText2 } from '@cian/ui-kit';

import { type ReactNode, useCallback, useMemo, useRef } from 'react';

import { useOpenableFilter } from '../Filters/hooks/useOpenableFilter';

import { UndergroundMapLayout } from './internal/components/UndergroundMapLayout';
import { UndergroundMapLineSelector } from './internal/containers/UndergroundMapLineSelector';
import { UndergroundMapModalHeaderContainer } from './internal/containers/UndergroundMapModalHeaderContainer';
import { UndergroundModalFooterContainer } from './internal/containers/UndergroundModalFooterContainer';
import { useUndergroundMapMicrofrontend } from './internal/hooks/useUndergroundMapMicrofrontend';

type UndergroundMapModalContainerProps = {
  open: boolean;
  onClose: () => void;
};

export function UndergroundMapModalContainer({ open, onClose }: UndergroundMapModalContainerProps): ReactNode {
  const microfrontendRef = useRef<ICianMFUIElement>(null);
  const { jsonQuery, onApply, onChange, status: metaStatus, count: metaCount } = useOpenableFilter();
  const { getApi, manifest, microfrontendParams, onRefetch, selectedStations, status } = useUndergroundMapMicrofrontend(
    microfrontendRef,
    jsonQuery,
    onChange,
  );

  const handleApply = useCallback(() => {
    onApply();
    onClose();
  }, [onApply, onClose]);

  const content = useMemo(() => {
    if (!open) {
      return null;
    }
    if (manifest) {
      return (
        <UndergroundMapLayout>
          <Microfrontend
            image={manifest.imageVersion}
            name={manifest.microfrontendName}
            parameters={microfrontendParams}
            ref={microfrontendRef}
            runtime={manifest.runtimeName}
          />
        </UndergroundMapLayout>
      );
    }

    if (status === 'pending' || status === 'idle') {
      return (
        <UndergroundMapLayout>
          <Spinner />
        </UndergroundMapLayout>
      );
    }

    return (
      <UndergroundMapLayout>
        <div>
          <UIText2>
            Во время загрузки произошла ошибка. Пожалуйста, проверьте своё интернет соединение и попробуйте ещё раз.
          </UIText2>
          <Button onClick={onRefetch}>Повторить</Button>
        </div>
      </UndergroundMapLayout>
    );
  }, [manifest, microfrontendParams, onRefetch, open, status]);

  return (
    <ModalWindow
      escape
      fixed
      portal
      content={content}
      footer={<UndergroundModalFooterContainer count={metaCount} status={metaStatus} onClick={handleApply} />}
      header={
        <UndergroundMapModalHeaderContainer jsonQuery={jsonQuery} onChange={onChange}>
          {open && manifest && <UndergroundMapLineSelector getApi={getApi} selectedStations={selectedStations} />}
        </UndergroundMapModalHeaderContainer>
      }
      maxWidth={1300}
      open={open}
      size="M"
      width="100%"
      onClose={onClose}
    />
  );
}
